import { useScrollReveal } from '@/hooks/useScrollReveal';

const cultureValues = [
  {
    icon: 'ri-shield-star-line',
    title: 'Ownership',
    desc: 'We hire people who act like founders. You own your domain end-to-end — from problem definition to solution to impact measurement. No one will tell you exactly what to do. That\'s the point.',
    color: 'primary',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Speed',
    desc: 'The cold chain doesn\'t wait. Harvests don\'t wait. We move fast — not recklessly, but with the conviction that shipping something good today beats polishing something perfect next month.',
    color: 'accent',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Data-Driven Decisions',
    desc: 'Ideas are hypotheses. We test them. Every product decision, operations change, and strategic move is backed by data — not hierarchy, not intuition, not the loudest voice in the room.',
    color: 'secondary',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Real-World Impact',
    desc: 'The code you write, the operations you optimise, the partnerships you build — they change real outcomes. A farmer gets a better price. A family eats safer food. That\'s the metric that matters.',
    color: 'primary',
  },
  {
    icon: 'ri-team-line',
    title: 'Humble & Hungry',
    desc: 'We hire people who are exceptional at what they do but approachable about it. No egos, no politics — just a shared obsession with building something that hasn\'t been built before in India.',
    color: 'accent',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'First Principles Thinking',
    desc: 'We don\'t copy what worked elsewhere. India\'s cold chain is unique — its harvest calendars, its geography, its economics. We think from scratch and build what\'s right for this market.',
    color: 'secondary',
  },
];

function getColorClasses(color: string) {
  if (color === 'primary') return { bg: 'bg-primary-100', text: 'text-primary-600', border: 'hover:border-primary-200/50', badge: 'bg-primary-50 text-primary-700' };
  if (color === 'accent') return { bg: 'bg-accent-100', text: 'text-accent-600', border: 'hover:border-accent-200/50', badge: 'bg-accent-50 text-accent-700' };
  return { bg: 'bg-secondary-100', text: 'text-secondary-600', border: 'hover:border-secondary-200/50', badge: 'bg-secondary-50 text-secondary-700' };
}

export default function CultureSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="culture" className="py-16 md:py-20 bg-secondary-50/30">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100/70 border border-secondary-200/50 mb-4">
            <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wider">Culture</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary-700 mb-4">
            Life at Freshhlock
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Six values that shape how we work, how we hire, and how we build — together.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cultureValues.map((v) => {
            const cls = getColorClasses(v.color);
            return (
              <div key={v.title} className={`group bg-card-50 rounded-xl border border-card-200 p-6 shadow-sm hover:shadow-md ${cls.border} transition-all cursor-default card-animate`}>
                <div className={`w-10 h-10 rounded-lg ${cls.bg} flex items-center justify-center mb-4 animate-icon-float icon-hover-pop`}>
                  <i className={`${v.icon} text-lg ${cls.text}`}></i>
                </div>
                <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}